// Libraries
import React from 'react';

// Supermove
import {Styled, Icon, Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useResponsive,
  ResponsiveType,
  useDrawer,
  useModal,
  useToast,
  ToastType,
  MutationError,
} from '@supermove/hooks';
import {
  User,
  UserModel,
  TeamModel,
  OrganizationModel,
  Organization,
  UserMoverPositionModel,
} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Phone, Json} from '@supermove/utils';

// App
import Table from '@shared/design/components/Table';
import {ColumnDefinitionType} from '@shared/design/components/Table/components/TableBuilder';
import Text from '@shared/design/components/Text';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import UserRole from '@shared/modules/User/enums/UserRole';
import UserStatus from '@shared/modules/User/enums/UserStatus';
import ImpersonateUserForm from '@shared/modules/User/forms/ImpersonateUserForm';
import useImpersonateUserMutation from '@shared/modules/User/hooks/useImpersonateUserMutation';
import StaffCommunicationDrawer from 'modules/Communication/components/StaffCommunicationDrawer';
import JobUserMoverPosition from 'modules/JobUser/components/JobUserMoverPosition';
import PasscodeCell from 'modules/Organization/Settings/Company/components/PasscodeCell';
import EditMoverDrawer from 'modules/Organization/Settings/Staff/components/EditMoverDrawer';
import TeamsTable from 'modules/Organization/Settings/Staff/components/TeamsTable';
import UpdateMoverStatusModal from 'modules/Organization/Settings/Staff/components/UpdateMoverStatusModal';
import {SelectedUserStatusType, TEAMS} from 'modules/Organization/Settings/Staff/types';
import DeleteUserModal from 'modules/User/components/DeleteUserModal';
import UpdateUserDrawer from 'modules/User/components/UpdateUserDrawer';

const TeamsTableContainer = Styled.View<{responsive: ResponsiveType}>`
  flex: 1;
  padding-horizontal: ${({responsive}) => (responsive.desktop ? 0 : 16)}px;
`;

const PositionsContainer = Styled.View`
  flex: 1;
  flex-direction: row;
  flex-wrap: wrap;
`;

const getMoverUsersTableColumnDefinition = ({
  viewer,
  organization,
  refetch,
  impersonateErrorToast,
}: {
  viewer: UserModel;
  organization: OrganizationModel;
  refetch: () => void;
  impersonateErrorToast: ToastType;
}): ColumnDefinitionType<UserModel>[] => [
  {
    flex: 4,
    minWidth: 170,
    headerLabel: 'Name',
    cellText: (moverUser) => moverUser.fullName,
    mobileOptions: {
      isInHeader: true,
      rank: 0,
    },
  },
  {
    flex: 3,
    headerLabel: 'Driver',
    cellText: (moverUser) => (moverUser.hasDriverMoverPosition ? 'Yes' : 'No'),
  },
  {
    flex: 8,
    minWidth: 250,
    headerLabel: 'Positions',
    cellComponent: (moverUser) => {
      return <Positions moverUser={moverUser} />;
    },
  },
  {
    flex: 3,
    isHidden: !organization.shouldCreateCommercialMoves,
    headerLabel: 'Branch Code',
    cellText: (moverUser) => moverUser.branchCode,
  },
  {
    flex: 3,
    minWidth: 150,
    headerLabel: 'Phone Number',
    cellText: (moverUser) =>
      moverUser.phoneNumber ? Phone.display(moverUser.phoneNumber) : undefined,
  },
  {
    flex: 3,
    headerLabel: 'Tablet Passcode',
    cellComponent: (moverUser) => {
      return moverUser.tabletPin === '' ? (
        <Text.Body>-</Text.Body>
      ) : (
        <PasscodeCell passcode={moverUser.tabletPin} />
      );
    },
  },
  {
    flex: 3,
    minWidth: 150,
    headerLabel: 'Can View Move Details',
    cellComponent: (moverUser) => {
      if (Json.parse(moverUser.features).VIEW_CUSTOMER_INFO) {
        return <Icon color={colors.gray.primary} size={Icon.Sizes.Large} source={Icon.Check} />;
      }
    },
  },
  {
    minWidth: 80,
    headerLabel: 'Actions',
    // @ts-expect-error TS(2322): Type '(moverUser: UserModel) => ({ text: string; onPress:...
    actions: (moverUser) => {
      // TODO(Hammad) remove any type
      const isEnabledSms = (Organization.getNotifications(organization) as any).allowEmployeeText;
      return [
        {
          text: 'Send SMS',
          onPress: ({handleOpen}) => handleOpen?.(),
          isDisabled: !isEnabledSms,
          desktopIcon: Icon.CommentDots,
          tooltip: !isEnabledSms
            ? 'Sending SMS to movers is disabled. Update this setting on the permissions tab.'
            : null,
          actionHook: {
            hook: useDrawer,
            hookArgument: {name: 'Staff Communication Drawer'},
            renderComponent: ({hookKey, isOpen, handleClose}) => {
              return (
                <StaffCommunicationAction
                  key={hookKey}
                  user={moverUser}
                  refetch={refetch}
                  viewer={viewer}
                  isOpen={isOpen}
                  handleClose={handleClose}
                />
              );
            },
          },
        },
        {
          text: 'Edit mover',
          onPress: ({handleOpen}) => handleOpen?.(),
          actionHook: {
            hook: useDrawer,
            hookArgument: {name: 'Edit Mover Drawer'},
            renderComponent: ({isOpen, handleClose, hookKey}) => {
              return (
                <EditMoverDrawer
                  key={hookKey}
                  moverUser={moverUser}
                  organization={organization}
                  isOpen={isOpen}
                  handleClose={handleClose}
                  handleSuccess={() => {
                    handleClose();
                    refetch();
                  }}
                />
              );
            },
          },
        },
        {
          text: 'Impersonate mover',
          isHidden: viewer.role !== UserRole.SUPER_ADMIN,
          onPress: ({handleSubmit}) => handleSubmit?.(),
          actionHook: {
            hook: useImpersonateUserMutation,
            hookArgument: {
              impersonateUserForm: ImpersonateUserForm.new({userId: moverUser.id}),
              onSuccess: () => {
                // TODO(Hammad) remove any type
                window.location = '/' as any;
              },
              onError: (errors: MutationError[]) => {
                impersonateErrorToast.handleToast();
                console.log({errors});
              },
            },
          },
        },
        {
          text: moverUser.status === UserStatus.INACTIVE ? 'Reactivate mover' : 'Deactivate mover',
          onPress: ({handleOpen}) => handleOpen?.(),
          color: moverUser.status !== UserStatus.INACTIVE && colors.red.warning,
          isHidden: moverUser.status === UserStatus.PENDING,
          actionHook: {
            hook: useModal,
            hookArgument: {name: 'Update Mover Status Modal'},
            renderComponent: ({hookKey, isOpen, handleClose}) => {
              return (
                <UpdateMoverStatusModal
                  key={hookKey}
                  isOpen={isOpen}
                  handleClose={handleClose}
                  moverUser={moverUser}
                  refetch={refetch}
                />
              );
            },
          },
        },
        {
          text: 'Delete mover',
          color: colors.red.warning,
          onPress: ({handleOpen}) => handleOpen?.(),
          isHidden: !UserRole.hasAdminPermissions(viewer.role),
          actionHook: {
            hook: useModal,
            hookArgument: {name: 'Delete User Modal'},
            renderComponent: ({hookKey, isOpen, handleClose}) => {
              return (
                <DeleteUserModal
                  key={hookKey}
                  isOpen={isOpen}
                  handleClose={handleClose}
                  userId={moverUser.id}
                  refetch={refetch}
                  userFullName={moverUser.fullName}
                  displayRole={'mover'}
                />
              );
            },
          },
        },
      ];
    },
  },
];

const Positions = ({moverUser}: {moverUser: UserModel}) => {
  const userMoverPositions = User.getSortedUserMoverPositions({
    userMoverPositions: moverUser.userMoverPositions,
  });

  return (
    <PositionsContainer>
      {userMoverPositions.map((userMoverPosition: UserMoverPositionModel, index: number) => (
        <React.Fragment key={userMoverPosition.id}>
          {index > 0 && <Space width={4} />}
          <JobUserMoverPosition.Badge
            isPrimary={userMoverPosition.isPrimary}
            color={colors.gray.secondary}
            name={userMoverPosition.moverPosition.name}
            isDisabled
            style={{marginVertical: 2}}
          />
        </React.Fragment>
      ))}
    </PositionsContainer>
  );
};

const StaffCommunicationAction = ({
  user,
  refetch,
  viewer,
  isOpen,
  handleClose,
}: {
  user: UserModel;
  refetch: () => void;
  viewer: UserModel;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const updateUserDrawer = useDrawer({name: 'Update User Drawer'});

  return (
    <React.Fragment>
      <StaffCommunicationDrawer
        user={user}
        isOpen={isOpen}
        handleClose={handleClose}
        handleMissingContactInfo={updateUserDrawer.handleOpen}
      />
      <UpdateUserDrawer
        key={updateUserDrawer.key}
        user={user}
        isOpen={updateUserDrawer.isOpen}
        handleClose={updateUserDrawer.handleClose}
        handleSuccess={() => {
          updateUserDrawer.handleClose();
          refetch();
        }}
        viewerRole={viewer.role}
      />
    </React.Fragment>
  );
};

const OfficeMoversTable = ({
  selectedUserStatus,
  moverUsers,
  crewTeams,
  organization,
  refetch,
  viewer,
}: {
  selectedUserStatus: SelectedUserStatusType;
  moverUsers: UserModel[];
  crewTeams: TeamModel[];
  organization: OrganizationModel;
  refetch: () => void;
  viewer: UserModel;
}) => {
  const responsive = useResponsive();
  const impersonateErrorToast = useToast({
    ToastComponent: ErrorToast,
    message: `Unable to impersonate staff member.`,
  });

  switch (selectedUserStatus) {
    case UserStatus.ACTIVE:
    case UserStatus.PENDING:
    case UserStatus.INACTIVE:
      return (
        <Table
          itemKey={'uuid'}
          columnDefinitions={getMoverUsersTableColumnDefinition({
            viewer,
            organization,
            refetch,
            impersonateErrorToast,
          })}
          emptyStateText={'No movers to display'}
          items={moverUsers}
          isScrollable
        />
      );
    case TEAMS:
      return (
        <TeamsTableContainer responsive={responsive}>
          <TeamsTable teams={crewTeams} refetch={refetch} viewer={viewer} isCrewTeam />
        </TeamsTableContainer>
      );
    default:
      return null;
  }
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OfficeMoversTable.fragment = gql`
  ${StaffCommunicationDrawer.fragment}
  ${UpdateUserDrawer.fragment}
  ${EditMoverDrawer.fragment}
  ${Organization.getNotifications.fragment}
  ${User.getSortedUserMoverPositions.fragment}
  ${TeamsTable.fragment}

  fragment OfficeMoversTable_User on User {
    id
    uuid
    fullName
    email
    phoneNumber
    position
    role
    status
    isActive
    features
    tabletPin
    branchCode
    hasDriverMoverPosition
    userMoverPositions {
      id
      isPrimary
      moverPosition {
        id
        name
      }
    }
    ...StaffCommunicationDrawer
    ...UpdateUserDrawer
    ...EditMoverDrawer
    ...User_getSortedUserMoverPositions
  }

  fragment OfficeMoversTable_Organization on Organization {
    id
    shouldCreateCommercialMoves
    crewTeams {
      id
      members {
        id
        role
      }
      ...TeamsTable
    }
    ...Organization_getNotifications
    ...EditMoverDrawer_Organization
  }

  fragment OfficeMoversTable_Viewer on User {
    id
    ...TeamsTable_Viewer
  }
`;

export default OfficeMoversTable;
